import { createHyperLinkToolParamsSchema } from "@redotech/redo-model/agents/platform-agents/settings/create-hyper-link";
import { AiChatContext } from "@redotech/redo-web/ai/chat/contexts/ai-chat-context";
import { ToolComponentProps } from "@redotech/redo-web/ai/chat/types/tool-component-props";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { useContext, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import remarkGfm from "remark-gfm";

export function CreateHyperLinkTool(props: ToolComponentProps) {
  const { sendToolResponse } = useContext(AiChatContext);

  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  const hyperLink = useMemo(() => {
    const data = props.confirmationData ?? props.input;
    if (data) {
      const result = createHyperLinkToolParamsSchema.safeParse(data);
      if (result.success) {
        return result.data;
      }
    }
    return undefined;
  }, [props.confirmationData, props.input]);

  if (cancelled) {
    return <Text>User cancelled navigation.</Text>;
  }

  if (submitted) {
    return <Text>User navigated to the setting.</Text>;
  }

  if (!hyperLink) {
    return (
      <Text color="error">
        AI generated a bad navigation link. Sorry about that.
      </Text>
    );
  }
  return (
    <Flex align="center" bgColor="primary" dir="column" gap="md" p="2xl">
      <div style={{ textAlign: "center", fontSize: "12px" }}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {hyperLink.messageToMerchant}
        </ReactMarkdown>
      </div>

      <Flex align="flex-start" dir="row" justify="space-evenly">
        <RedoButton
          hierarchy="secondary"
          onClick={async () => {
            setCancelled(true);
            await sendToolResponse({
              toolCallId: props.toolCallId,
              toolName: props.toolName,
              result: { navigated: false },
            });
          }}
          size="sm"
          text="Cancel"
        />
        <RedoButton
          hierarchy="primary"
          onClick={async () => {
            setSubmitted(true);

            const [link, parameters] = hyperLink.hyperLink.split("?");
            const url = {
              pathname: `/${link}`,
              search: `?highlightText=${encodeURIComponent(hyperLink.regexToMatch)}${parameters ? `&${parameters}` : ""}`,
            };
            navigate(url, { replace: true });

            await sendToolResponse({
              toolCallId: props.toolCallId,
              toolName: props.toolName,
              result: { navigated: true },
            });
          }}
          text="Go to setting"
        />
      </Flex>
    </Flex>
  );
}
